"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getInitials } from "@/lib/utils";
import { DataSiswa } from "@/types";
import { ArrowUpDown, FileCheck2 } from "lucide-react";
import { Link } from "react-router-dom";

export const columns: ColumnDef<DataSiswa>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <div className="px-1">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="border-muted-foreground/50 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="border-muted-foreground/50 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "verifikasi",
    header: () => <div className="text-center font-semibold">Aksi</div>,
    cell: ({ row }) => {
      const nomorPendaftaran =
        row.original?.data_pendaftaran?.[0]?.nomor_pendaftaran;

      // Ambil path dan search saat ini (misal: /peserta?page=1&search=budi)
      // Kita gunakan encodeURIComponent agar karakter seperti '?' dan '&' tidak merusak URL detail
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const returnTo = encodeURIComponent(`${currentPath}${currentSearch}`);

      return (
        <div className="flex justify-center">
          <Link
            target="_blank"
            // Kirim parameter 'no' dan parameter 'callback'
            to={`/verifikator/raport/verifikasi-raport-peserta/view?no=${nomorPendaftaran}&returnTo=${returnTo}`}
          >
            <Button variant="outline" size="sm">
              <FileCheck2 className="w-3.5 h-3.5 mr-1.5" />
              Verifikasi
            </Button>
          </Link>
        </div>
      );
    },
  },
  {
    accessorKey: "status_validasi",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Validasi
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.original?.status_validasi;

      if (status === "REKON") {
        return (
          <Badge
            variant="outline"
            className="bg-destructive/10 text-destructive border-destructive/20 dark:text-red-400 font-medium"
          >
            Rekon
          </Badge>
        );
      }

      if (!status) {
        return (
          <Badge
            variant="outline"
            className="bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400 font-medium whitespace-nowrap"
          >
            Belum Divalidasi
          </Badge>
        );
      }

      return (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400 font-medium whitespace-nowrap"
        >
          Sudah Divalidasi
        </Badge>
      );
    },
  },
//     {
//     accessorKey: "catatan_verifikator",
//     header: ({ column }) => {
//       return (
//         <Button
//           variant="ghost"
//           size="sm"
//           className="-ml-3 h-8 font-semibold"
//           onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
//         >
//           Catatan Verifikator
//           <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
//         </Button>
//       );
//     },
// cell: ({ row }) => {

//   const berkasRaport = (row.original?.data_pendaftaran?.[0]?.data_berkas ?? []).find((berkas) => {
//     return berkas.sys_berkas?.jenis_berkas?.slug_jenis_berkas === "Berkas_Nilai_Rapor"
//   });

//   // Jika berkas tidak ditemukan, tampilkan placeholder atau null
//   if (!berkasRaport) {
//     return <div className="text-gray-400">-</div>;
//   }

//   return (
//     <div>
//       <h1 className="font-semibold">
//         {berkasRaport?.user_verifikator?.nama_lengkap ?? ""}
//       </h1>
//       <p className="italic text-sm">
//         {berkasRaport?.catatan_verifikator ?? ""}
//       </p>
//     </div>
//   );
// },
//   },
  {
    accessorKey: "data_pendaftaran.nomor_pendaftaran",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          No. Pendaftaran
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const nomor = row.original?.data_pendaftaran?.[0]?.nomor_pendaftaran;
      return <div className="font-mono text-sm">{nomor ?? "-"}</div>;
    },
  },
  {
    accessorKey: "nama_lengkap",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nama Lengkap
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div
          className="font-medium text-foreground capitalize truncate max-w-[200px]"
          title={row.getValue("nama_lengkap")}
        >
          {row.getValue("nama_lengkap") || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "sekolah.nama_sekolah",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Asal Sekolah
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const sekolah = row.original?.sekolah?.nama_sekolah;
      return (
        <div
          className="truncate max-w-[200px] text-muted-foreground"
          title={sekolah}
        >
          {sekolah || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_pendaftaran[0].unitkerja.nama",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold whitespace-nowrap"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Program Studi
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const prodi = row.original?.data_pendaftaran?.[0]?.unitkerja?.nama;
      return (
        <div
          className="text-sm text-foreground truncate max-w-[180px]"
          title={prodi}
        >
          {prodi || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div
          className="text-sm text-muted-foreground truncate max-w-[180px]"
          title={row.getValue("email")}
        >
          {row.getValue("email") || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "handphone",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold whitespace-nowrap"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          No Handphone
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="text-sm text-muted-foreground">
          {row.getValue("handphone") || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "jenis_kelamin",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          L/P
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 text-muted-foreground" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const jk = row.getValue("jenis_kelamin") as string;
      return (
        <div className="text-center font-medium text-muted-foreground">
          {jk === "Laki-laki" || jk === "L"
            ? "L"
            : jk === "Perempuan" || jk === "P"
              ? "P"
              : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "path_foto",
    header: () => <div className="text-center font-semibold">Foto</div>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <Avatar className="h-9 w-9 border border-border shadow-sm">
            <AvatarImage
              src={row.getValue("path_foto")}
              alt={row.original.nama_lengkap || "Foto"}
              className="object-cover"
            />
            <AvatarFallback className="bg-muted text-muted-foreground text-xs font-medium">
              {getInitials(row.original.nama_lengkap || "")}
            </AvatarFallback>
          </Avatar>
        </div>
      );
    },
    enableSorting: false,
  },
];
